import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Reusable read-only request details dialog.
 *
 * Renders a header (title + description), a compact label/value details grid,
 * and optional slots for page-specific action UI (`children`) and footer buttons.
 *
 * Props:
 * - open, onOpenChange      : Dialog visibility control.
 * - title                   : Node rendered inside DialogTitle (may include an icon).
 * - description             : Node rendered inside DialogDescription.
 * - fields                  : Array of detail descriptors. Falsy entries are ignored, so
 *                             callers can inline conditions, e.g. `cond && { label, value }`.
 *                             Each field: {
 *                               label,                // string
 *                               value,                // node or string
 *                               fullWidth,            // span the whole row + top border
 *                               valueClassName,       // override the <p> classes
 *                               render,               // (value) => node, full custom render
 *                             }
 * - children                : Action UI rendered below the details grid (e.g. comment box,
 *                             decision radios). Each page owns its own approve/reject logic.
 * - footer                  : Footer buttons (rendered inside a DialogFooter).
 * - footerClassName         : Override the footer layout classes.
 * - contentClassName        : Override the DialogContent classes.
 */
const RequestDetailsDialog = ({
  open,
  onOpenChange,
  title,
  description,
  fields = [],
  children,
  footer,
  footerClassName = "mt-4 flex flex-col sm:flex-row-reverse sm:justify-start gap-2",
  contentClassName = "sm:max-w-lg md:max-w-xl max-h-[90vh] overflow-y-auto",
}) => {
  const visibleFields = fields.filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={contentClassName}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {visibleFields.length > 0 && (
          <div className="space-y-4 pt-2">
            <div className="p-4 border border-border rounded-lg bg-muted/40 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {visibleFields.map((field, idx) => (
                <div
                  key={field.label ?? idx}
                  className={
                    field.fullWidth
                      ? "col-span-full pt-2 border-t border-border"
                      : "col-span-1"
                  }
                >
                  <span className="text-xs text-muted-foreground block">
                    {field.label}
                  </span>
                  {field.render ? (
                    field.render(field.value)
                  ) : (
                    <p className={field.valueClassName || "font-medium text-sm"}>
                      {field.value}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {children}

        {footer && <DialogFooter className={footerClassName}>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};

export default RequestDetailsDialog;
